// Shared promo constants (client backlog 14.07, P2-40): the bot username is
// referenced from copy in several surfaces - keep it in one place.
export const BOT_USERNAME = "@stingf1bot";

/* ---------------------------------------------------------------------------
   Promo config bridge (v9 mechanics package).

   The team x2 threshold and the referral reward sizes are owned by
   `config/promo.config.json` (backend truth). The Mini App used to hard-code
   "4" in ~15 copy strings and progress counters, so a config change silently
   made the UI lie. `vite.config.ts` inlines the relevant slice of that same
   file at build time as `__PROMO_CONFIG__`; nothing here invents values.

   Two sources, on purpose:
   - build-time config: static copy (catalog strings) and the referral gate;
   - live API (`state.team`): the numeric progress counters, so a deployed
     backend with a different threshold (version skew) still renders honestly.
--------------------------------------------------------------------------- */

export type PromoConfig = {
  teamBoostThreshold: number;
  referral: {
    enabled: boolean;
    joinAttemptBonus: number;
    firstReceiptTicketBonus: number;
  };
};

declare const __PROMO_CONFIG__: PromoConfig;

/* Build-time fallback: only reached when a consumer bundles this module
   without the vite define (e.g. a bare tsc/node import in a contract test). */
const FALLBACK: PromoConfig = {
  teamBoostThreshold: 4,
  referral: { enabled: false, joinAttemptBonus: 0, firstReceiptTicketBonus: 0 },
};

export const PROMO: PromoConfig =
  typeof __PROMO_CONFIG__ === "undefined" ? FALLBACK : __PROMO_CONFIG__;

/** Team members that must finish the mini-game in one Tashkent day for x2. */
export const TEAM_BOOST_THRESHOLD: number = Math.max(1, Number(PROMO.teamBoostThreshold) || FALLBACK.teamBoostThreshold);

/** Two-stage referral rewards; `enabled` gates every referral promise in copy. */
export const REFERRAL = {
  enabled: Boolean(PROMO.referral?.enabled),
  joinAttemptBonus: Number(PROMO.referral?.joinAttemptBonus) || 0,
  firstReceiptTicketBonus: Number(PROMO.referral?.firstReceiptTicketBonus) || 0,
};

/** Referral copy may only promise a reward when the config actually pays one. */
export const REFERRAL_REWARDS_VISIBLE: boolean =
  REFERRAL.enabled && (REFERRAL.joinAttemptBonus > 0 || REFERRAL.firstReceiptTicketBonus > 0);

type TeamLike = {
  playedCount?: number | null;
  missingForBoost?: number | null;
  boostActive?: boolean | null;
} | null | undefined;

/**
 * Threshold to render for this user.
 *
 * `state.team` exposes `playedCount` and `missingForBoost`, and the backend
 * derives both from the same `rewardedPlayersPerDay`; while the boost is not
 * active their sum IS the live threshold, which beats the build-time constant
 * under a stale-bundle/new-backend skew. Once the boost is active
 * `missingForBoost` is 0 and carries no information, so we fall back to config.
 */
export function teamBoostThreshold(team: TeamLike): number {
  const played = Math.max(0, Number(team?.playedCount) || 0);
  const missing = Math.max(0, Number(team?.missingForBoost) || 0);
  if (missing > 0) return played + missing;
  return TEAM_BOOST_THRESHOLD;
}

/** "played/threshold" progress, clamped so a full team never renders "4/2". */
export function teamBoostProgress(team: TeamLike): { played: number; threshold: number; text: string } {
  const threshold = teamBoostThreshold(team);
  const played = Math.max(0, Number(team?.playedCount) || 0);
  return { played, threshold, text: `${Math.min(played, threshold)}/${threshold}` };
}

function ruPlural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** "2 участника" / "5 участников" / "2 ishtirokchi" - agreement-safe for any threshold. */
export function playersLabel(n: number, lang: string): string {
  if (lang === "uz") return `${n} ishtirokchi`;
  return `${n} ${ruPlural(n, "участник", "участника", "участников")}`;
}

/** "1 попытка" / "2 попытки" / "1 ta urinish". */
export function attemptsLabel(n: number, lang: string): string {
  if (lang === "uz") return `${n} ta urinish`;
  return `${n} ${ruPlural(n, "попытка", "попытки", "попыток")}`;
}

/** "1 шанс" / "2 шанса" / "1 ta imkoniyat". */
export function chancesLabel(n: number, lang: string): string {
  if (lang === "uz") return `${n} ta imkoniyat`;
  return `${n} ${ruPlural(n, "шанс", "шанса", "шансов")}`;
}
